import { MessagingRepository } from '@Engage.Data/repositories/messaging/MessagingRepository';
import { UserStore } from '../../../auth/model/UserStore';
/**
 * Mirrors ConneXt v2 Messaging.executeAction() exactly.
 *
 * Flow:
 * 1. Guard: logged-in users only
 * 2. Get locationCode from [data-tmpl-action-name="Location code"]
 * 3. Get registrationId + subscriptionsIds from UserStore
 * 4. Empty [data-mg2-container="messages"]
 * 5. Resolve messages (localStorage cache → API)
 * 6. For each message: clone the container's own HTML, set text, append
 */
export class MessagingEngine {
    static isUserLoggedIn() {
        return UserStore.isLoggedIn();
    }
    /**
     * Main entry point — call after the template HTML has been rendered into the DOM.
     * Pass the root element of the rendered action template.
     *
     * Mirrors v2:
     *   let messagesContainer = dom.find('[data-mg2-container="messages"]', actionTemplate.getElement());
     *   let locationCode      = dom.text(dom.find('[data-tmpl-action-name="Location code"]', ...));
     *   dom.empty(messagesContainer);
     *   this.getUserMessages(...).then(messages => { each(messages, ...) });
     */
    static async loadAndRender(templateElement) {
        const locationCode = this.getLocationCode(templateElement);
        if (!locationCode)
            return;
        const messagesContainer = templateElement.querySelector('[data-mg2-container="messages"]');
        if (!messagesContainer)
            return;
        const { registrationId, subscriptionsIds } = this.getUserIdentity();
        const messages = await this.getUserMessages(locationCode, registrationId, subscriptionsIds);
        // Empty the container before injecting — mirrors dom.empty(messagesContainer)
        messagesContainer.innerHTML = '';
        // Clone the container's own outer HTML as the per-message template node.
        // Mirrors v2: let messageElement = dom.create(dom.html(messagesContainer))
        // dom.html() returns outerHTML in v2, so we snapshot it before clearing.
        // We re-use the default message node structure for cloning.
        const defaultMessageHtml = this.getDefaultMessageHtml(templateElement);
        messages.forEach((message) => {
            const node = document.createElement('div');
            node.innerHTML = defaultMessageHtml;
            const cloned = node.firstElementChild;
            if (!cloned)
                return;
            // Set the message text — mirrors dom.html(dom.find('span.user-message__message-text', messageElement), message)
            const textEl = cloned.querySelector('span.user-message__message-text');
            if (textEl)
                textEl.textContent = message;
            messagesContainer.appendChild(cloned);
        });
    }
    // ─── Message resolution (cache → API) ────────────────────────────────────
    // Mirrors v2 Messaging.getUserMessages()
    static async getUserMessages(locationCode, registrationId, subscriptionsIds) {
        // Check localStorage cache (invalidated when expiry cookie is absent)
        const storedMessages = MessagingRepository.getCachedMessages();
        if (storedMessages && storedMessages[locationCode]) {
            return storedMessages[locationCode];
        }
        // Cache miss — fetch from API
        const params = { locationCode, registrationId };
        if (subscriptionsIds && subscriptionsIds.length) {
            params.subscriptionsIds = subscriptionsIds;
        }
        try {
            const response = await MessagingRepository.fetchUserMessages(params);
            const messages = Array.isArray(response) ? response : [];
            // Store in cache — mirrors v2: messages[locationCode] = response; ActionRepository.setMessagingData(messages)
            const store = storedMessages || {};
            store[locationCode] = messages;
            MessagingRepository.setCachedMessages(store);
            MessagingRepository.setExpiryCookie();
            return messages;
        }
        catch (err) {
            console.error('[MessagingEngine] fetchUserMessages failed', err);
            return [];
        }
    }
    // ─── DOM helpers ──────────────────────────────────────────────────────────
    /**
     * Reads locationCode from [data-tmpl-action-name="Location code"] inside the template.
     * Mirrors v2: dom.text(dom.find('[data-tmpl-action-name="Location code"]', actionTemplate.getElement())[0])
     */
    static getLocationCode(element) {
        var _a;
        const el = element.querySelector('[data-tmpl-action-name="Location code"]');
        return ((_a = el === null || el === void 0 ? void 0 : el.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || null;
    }
    /**
     * Returns the outer HTML of the default message node to use as a clone template.
     * Mirrors v2: dom.html(messagesContainer) — which is the outerHTML of the container,
     * then dom.create() wraps it. We use [data-mg2-messaging="default"] as the template node.
     */
    static getDefaultMessageHtml(templateElement) {
        var _a;
        const defaultNode = templateElement.querySelector('[data-mg2-messaging="default"]');
        return (_a = defaultNode === null || defaultNode === void 0 ? void 0 : defaultNode.outerHTML) !== null && _a !== void 0 ? _a : '<div class="user-message user-message_default"><span class="user-message__message-icon" aria-hidden="true">&#9432;</span><span class="user-message__message-text"></span></div>';
    }
    // ─── User identity ────────────────────────────────────────────────────────
    // Mirrors v2:
    //   registrationId  = User.getCustomerRegistrationId()
    //   subscriptionsIds = compact(pluck(User.getSubscriptions(), "SubscriptionId"))
    static getUserIdentity() {
        var _a, _b;
        try {
            const userData = UserStore.getUser();
            if (!userData)
                return { registrationId: null, subscriptionsIds: [] };
            const registrationId = (_a = userData.CustomerRegistrationId) !== null && _a !== void 0 ? _a : null;
            const subscriptions = (_b = userData.Subscriptions) !== null && _b !== void 0 ? _b : [];
            const subscriptionsIds = subscriptions
                .map((s) => s.SubscriptionId)
                .filter((id) => id != null && !isNaN(Number(id)))
                .map(Number);
            return { registrationId, subscriptionsIds };
        }
        catch (_c) {
            return { registrationId: null, subscriptionsIds: [] };
        }
    }
}
